// packages/report-components/src/PageDensityContext.tsx
//
// The seam where a surface tells the components that they are drawing ON A
// REPORT PAGE, and what their parts measure there (plan 2026-09-19-a-report-
// page-has-a-house-style, step B1). Same shape as BrandContext: a provider the
// surface mounts, a hook the components read, null when there is none.
//
// Why a context and not a stylesheet rule. A page cell clips and nothing
// reflows, so the server budgets a block's content from pixel numbers: rows
// that fit a table, items that fit a list. Those numbers live in
// `services/composition/layout.py` ("Page density") and are emitted with the
// layout spec. For the budget to be true the components must DRAW those
// numbers, and most of what they draw is inline style (`KeyTakeaways`,
// `RecommendationList`, `DataPoint`, the frames' containers), which no
// stylesheet rule outranks without `!important`. The page-formats plan settled
// the general form on 2026-09-07 (step B4b): per-element geometry is DATA, not
// style. So the numbers arrive as data, and a component on a page writes them
// into the same inline styles it already owns.
//
// It also keeps the email-bundle guard out of the question altogether: nothing
// here is a selector, so nothing here can reach a 640px mail. A flowing report
// and the dashboard mount no provider and draw exactly as before.
//
// What a component does with it is its own business, with one shared rule: on
// a page, the drawn height of a repeated part (a table row, a list item) is
// the spec's number, by construction. `height` on a `<tr>` is a MINIMUM in CSS
// (CSS 2.2 §17.5.3; measured in Chromium: a two-line cell under
// `tr { height: 30px }` draws 40px), so a row's height is fixed by a wrapper
// inside every cell with a px `height`, a px `line-height` and
// `overflow: hidden`, never by the row. The browser test in
// `apps/artifact-sandbox/measure` holds every one of these to the spec.

import * as React from "react";

/** The page-density numbers, exactly as `layout_spec_dict()["page_density"]`
 * emits them, with `table_max_columns` already resolved for the document's
 * format. Every field is a positive integer number of CSS px (or a count). */
export type PageDensity = {
  readonly frame_pad_px: number;
  readonly frame_border_px: number;
  readonly frame_title_px: number;
  readonly frame_gap_px: number;
  readonly caption_line_px: number;
  /** A block subtitle (plan 2026-10-16-a-block-subtitle): its type size, its
   *  fixed line and how many lines a frame always budgets for it. */
  readonly subtitle_font_px: number;
  readonly subtitle_line_px: number;
  readonly subtitle_lines: number;
  readonly table_font_px: number;
  readonly table_cell_line_px: number;
  readonly table_cell_pad_y_px: number;
  readonly table_cell_pad_x_px: number;
  readonly table_rule_px: number;
  readonly table_row_px: number;
  readonly table_row_2line_px: number;
  readonly table_header_font_px: number;
  readonly table_header_line_px: number;
  readonly table_header_row_px: number;
  readonly table_max_columns: number;
  readonly list_heading_px: number;
  readonly list_heading_gap_px: number;
  readonly list_title_line_px: number;
  readonly list_body_line_px: number;
  readonly list_title_body_gap_px: number;
  readonly list_item_pad_y_px: number;
  readonly list_rule_px: number;
  readonly list_max_body_lines: number;
  readonly prose_font_px: number;
  readonly prose_line_px: number;
  readonly eyebrow_line_px: number;
  readonly eyebrow_gap_px: number;
  readonly band_pad_x_px: number;
  readonly band_pad_y_px: number;
  readonly thesis_font_px: number;
  readonly thesis_line_px: number;
  readonly thesis_max_lines: number;
  readonly thesis_gap_px: number;
  readonly bullets_gap_px: number;
  readonly kpi_pad_px: number;
  readonly kpi_pad_narrow_px: number;
  readonly kpi_title_font_px: number;
  readonly kpi_title_line_px: number;
  readonly kpi_title_font_narrow_px: number;
  readonly kpi_title_line_narrow_px: number;
  readonly kpi_title_lines: number;
  readonly kpi_title_gap_px: number;
  readonly kpi_value_font_narrow_px: number;
  readonly kpi_narrow_below_px: number;
  /** The strip's vertical padding (form B, plan 2026-09-20). There is no
   *  horizontal one: the hairlines run the cell's full width. */
  readonly kpi_strip_pad_y_px: number;
  /** The strip's own lines. The component draws FROM these so the server's
   *  budget and the drawing cannot disagree (same rule as the table row). */
  readonly kpi_value_line_px: number;
  readonly kpi_note_line_px: number;
  readonly kpi_note_lines: number;
  readonly kpi_row_line_px: number;
  readonly kpi_row_gap_px: number;
  readonly kpi_rows_gap_px: number;
};

const FIELDS: readonly (keyof PageDensity)[] = [
  "frame_pad_px",
  "frame_border_px",
  "frame_title_px",
  "frame_gap_px",
  "caption_line_px",
  "subtitle_font_px",
  "subtitle_line_px",
  "subtitle_lines",
  "table_font_px",
  "table_cell_line_px",
  "table_cell_pad_y_px",
  "table_cell_pad_x_px",
  "table_rule_px",
  "table_row_px",
  "table_row_2line_px",
  "table_header_font_px",
  "table_header_line_px",
  "table_header_row_px",
  "table_max_columns",
  "list_heading_px",
  "list_heading_gap_px",
  "list_title_line_px",
  "list_body_line_px",
  "list_title_body_gap_px",
  "list_item_pad_y_px",
  "list_rule_px",
  "list_max_body_lines",
  "prose_font_px",
  "prose_line_px",
  "eyebrow_line_px",
  "eyebrow_gap_px",
  "band_pad_x_px",
  "band_pad_y_px",
  "thesis_font_px",
  "thesis_line_px",
  "thesis_max_lines",
  "thesis_gap_px",
  "bullets_gap_px",
  "kpi_pad_px",
  "kpi_pad_narrow_px",
  "kpi_title_font_px",
  "kpi_title_line_px",
  "kpi_title_font_narrow_px",
  "kpi_title_line_narrow_px",
  "kpi_title_lines",
  "kpi_title_gap_px",
  "kpi_value_font_narrow_px",
  "kpi_narrow_below_px",
  "kpi_strip_pad_y_px",
  "kpi_value_line_px",
  "kpi_note_line_px",
  "kpi_note_lines",
  "kpi_row_line_px",
  "kpi_row_gap_px",
  "kpi_rows_gap_px",
];

/** No page part is taller than a page. A number outside this range is not a
 * density, it is a broken spec, and drawing from it would be worse than
 * drawing the flowing form. */
const MAX_PX = 400;

/**
 * The emitted density for one page format, or null.
 *
 * Refuses rather than repairs, the way `brandStyleRule` refuses a colour that
 * is not a colour: every field must be a positive integer within range, and
 * `table_max_columns` must name the format. These numbers become inline
 * `height` and `line-height` values; the generated constant is the only
 * producer today, and this check is what keeps that a fact about the code
 * rather than a habit.
 */
export function pageDensityFor(
  raw: unknown,
  formatKey: string | null | undefined,
): PageDensity | null {
  if (!raw || typeof raw !== "object" || !formatKey) return null;
  const source = raw as Record<string, unknown>;
  const columnsByFormat = source.table_max_columns;
  if (!columnsByFormat || typeof columnsByFormat !== "object") return null;
  // Own keys only: `"constructor"` must not resolve to a function.
  if (!Object.prototype.hasOwnProperty.call(columnsByFormat, formatKey)) return null;
  const resolved: Record<string, unknown> = {
    ...source,
    table_max_columns: (columnsByFormat as Record<string, unknown>)[formatKey],
  };
  const out: Record<string, number> = {};
  for (const field of FIELDS) {
    const value = resolved[field];
    if (typeof value !== "number" || !Number.isInteger(value) || value < 1 || value > MAX_PX) {
      return null;
    }
    out[field] = value;
  }
  return out as unknown as PageDensity;
}

const PageDensityContext = React.createContext<PageDensity | null>(null);

export type PageDensityProviderProps = {
  density: PageDensity | null | undefined;
  /** Optional in the type only so `React.createElement(Provider, props, ...children)`
   * type-checks (the sandbox's renderer is written without JSX). */
  children?: React.ReactNode;
};

export function PageDensityProvider({ density, children }: PageDensityProviderProps): React.ReactElement {
  return <PageDensityContext.Provider value={density ?? null}>{children}</PageDensityContext.Provider>;
}

/** The page's density, or null on a surface that draws no page: a flowing
 * report, a mail, the dashboard. Null means "draw as you always did". */
export function usePageDensity(): PageDensity | null {
  return React.useContext(PageDensityContext);
}

// ---- the arithmetic the server does, restated for the drawing layer --------
//
// Not a second source of truth: these are the SAME sums as `layout.py`'s
// `page_frame_chrome_px`, `page_table_row_px` and `page_list_item_px`, over the
// same emitted numbers, and the browser test asserts the drawn boxes against
// them. They exist so a component never writes `18 + 4 + 2 * 20` by hand.

export function pageTableRowPx(density: PageDensity, labelLines: 1 | 2 = 1): number {
  return labelLines === 2 ? density.table_row_2line_px : density.table_row_px;
}

/** What a scorecard DRAWS on a page, in px — the same sum as
 *  `layout.py::page_kpi_height_px` over the same emitted numbers, so a
 *  fixture can ask for the cell the server would give it instead of naming a
 *  row count by hand. Since the strip stacks the comparisons under the value,
 *  a KPI's height depends on what it holds. */
export function pageKpiHeightPx(
  density: PageDensity,
  opts: { comparisons?: number; ownWindow?: boolean; note?: boolean } = {},
): number {
  let px =
    2 * density.kpi_strip_pad_y_px +
    density.kpi_title_line_px * density.kpi_title_lines +
    density.kpi_title_gap_px +
    density.kpi_value_line_px;
  if (opts.ownWindow) px += density.caption_line_px;
  if (opts.note) px += density.kpi_note_line_px * density.kpi_note_lines;
  const rows = Math.max(0, Math.floor(opts.comparisons ?? 1));
  if (rows > 0) {
    px += density.kpi_rows_gap_px + rows * density.kpi_row_line_px + (rows - 1) * density.kpi_row_gap_px;
  }
  return px;
}

export function pageListItemPx(density: PageDensity, bodyLines: number): number {
  const lines = Math.max(0, Math.min(density.list_max_body_lines, Math.floor(bodyLines)));
  const body = lines > 0 ? density.list_title_body_gap_px + lines * density.list_body_line_px : 0;
  return density.list_title_line_px + body + 2 * density.list_item_pad_y_px + density.list_rule_px;
}

export function pageFrameChromePx(
  density: PageDensity,
  opts: { title?: boolean; caption?: boolean; subtitle?: boolean } = {},
): number {
  const { title = true, caption = false, subtitle = false } = opts;
  let chrome = 2 * (density.frame_pad_px + density.frame_border_px);
  if (title) chrome += density.frame_title_px + density.frame_gap_px;
  if (caption) chrome += density.caption_line_px;
  // Both budgeted lines whenever there is a subtitle, as
  // `layout.page_frame_chrome_px(subtitle=True)` counts them.
  if (subtitle) chrome += density.subtitle_line_px * density.subtitle_lines;
  return chrome;
}

export function pageListChromePx(density: PageDensity, opts: { caption?: boolean } = {}): number {
  // No frame: a list on a page draws no card (2026-09-22). Its label and the
  // gap under it are the whole of its chrome, and the server prices it with
  // the same two numbers (`layout.page_list_chrome_px`).
  return (
    (opts.caption ? density.caption_line_px : 0) +
    density.list_heading_px +
    density.list_heading_gap_px
  );
}
